import React, { useCallback, useState, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'
import { Venue } from '../types/forms'

// Option type extends Venue with react-select required fields
interface VenueOption extends Venue {
    value: string
    label: string
}

export interface VenueSelectProps {
    value: Venue | Venue[] | null
    onChange: (venue: Venue | Venue[] | null) => void
    complexId?: string // Optional: pre-populate venues from this complex
    isMulti?: boolean
    isClearable?: boolean
    placeholder?: string
    className?: string
    isDisabled?: boolean
    isModified?: boolean
}

// Build venue label: "Court, Complex (City)" format
const buildVenueLabel = (v: Venue): string => {
    const parts = [v.name]
    if (v.complex_name) {
        parts.push(v.complex_name)
    }
    let label = parts.join(', ')
    if (v.city) {
        label += ` (${v.city})`
    }
    return label
}

// Convert Venue to react-select option (exported for use in forms)
export const toOption = (v: Venue): VenueOption => ({
    ...v,
    value: v.tc_id,
    label: buildVenueLabel(v)
})

/**
 * Reusable venue select component with async loading
 * Supports both single and multi-select modes
 * When complexId is provided, pre-populates with venues from that complex
 */
const VenueSelect: React.FC<VenueSelectProps> = ({
    value,
    onChange,
    complexId,
    isMulti = false,
    isClearable = true,
    placeholder = 'Search courts...',
    className = '',
    isDisabled = false,
    isModified = false
}) => {
    // Store pre-loaded venues for the complex
    const [complexVenues, setComplexVenues] = useState<VenueOption[]>([])

    // Transform API response to Venue format
    const transformVenues = useCallback((rawVenues: any[]): VenueOption[] => {
        const venues: Venue[] = rawVenues.map((item: any) => ({
            tc_id: item.tc_venue_id,
            ext_id: item.ext_venue_id,
            name: item.tc_name || item.ext_name || 'Unknown Court',
            city: item.tc_city || item.ext_city || '',
            country_code: item.tc_country_code || item.ext_country_code || '',
            timezone: item.tc_timezone || item.ext_timezone || '',
            complex_name: item.complex_name || ''
        }))
        return venues.map(toOption)
    }, [])

    // Load venues for the complex when complexId changes
    useEffect(() => {
        if (complexId) {
            api.get('/venues', { complex_id: complexId, limit: 50 })
                .then(response => {
                    const rawVenues = response?.venues || []
                    setComplexVenues(transformVenues(rawVenues))
                })
                .catch(error => {
                    console.error('Failed to load complex venues:', error)
                    setComplexVenues([])
                })
        } else {
            setComplexVenues([])
        }
    }, [complexId, transformVenues])

    const fetchVenuesData = useCallback(async (inputValue: string): Promise<VenueOption[]> => {
        // If no search input, return complex venues (if any)
        if (inputValue.length < 2) {
            return complexVenues
        }

        try {
            // Search all venues (not filtered by complex)
            const response = await api.get('/venues', { name: inputValue, limit: 50 })
            const rawVenues = response?.venues || []
            const searchResults = transformVenues(rawVenues)

            // If we have complex venues, prioritize them in search results
            if (complexVenues.length > 0) {
                const searchLower = inputValue.toLowerCase()

                // Filter complex venues that match the search
                const matchingComplexVenues = complexVenues.filter(v =>
                    v.label.toLowerCase().includes(searchLower)
                )

                // Remove complex venues from search results to avoid duplicates
                const complexIds = new Set(complexVenues.map(v => v.tc_id))
                const otherResults = searchResults.filter(v => !complexIds.has(v.tc_id))

                // Return: matching complex venues first, then other search results
                return [...matchingComplexVenues, ...otherResults]
            }

            return searchResults
        } catch (error) {
            console.error('Failed to load venues:', error)
            return []
        }
    }, [complexVenues, transformVenues])

    const loadVenues = useDebouncedCallback(fetchVenuesData, 300)

    // Convert value to option format
    const selectValue = value
        ? (isMulti ? (value as Venue[]).map(toOption) : toOption(value as Venue))
        : null

    // Determine default options: show complex venues if available, otherwise false
    const hasComplexVenues = complexVenues.length > 0
    const defaultOptions = hasComplexVenues ? complexVenues : false

    return (
        <div className={isModified ? selectStyles.modified : ''}>
            <AsyncSelect<VenueOption, typeof isMulti>
                key={complexId || 'no-complex'} // Force re-render when complexId changes
                value={selectValue}
                onChange={onChange as any}
                loadOptions={loadVenues}
                defaultOptions={defaultOptions}
                openMenuOnFocus={hasComplexVenues} // Show venues on focus when complex venues available
                openMenuOnClick={hasComplexVenues} // Also open on click if we have venues to show
                isMulti={isMulti}
                isClearable={isClearable}
                isDisabled={isDisabled}
                placeholder={placeholder}
                className={`${selectStyles.reactSelect} ${className}`}
                classNamePrefix="react-select"
                cacheOptions={false} // Disable cache to ensure fresh results when complex changes
                getOptionValue={(option) => option.tc_id}
                getOptionLabel={(option) => buildVenueLabel(option)}
                noOptionsMessage={({ inputValue }) =>
                    inputValue.length < 2
                        ? hasComplexVenues
                            ? "Type to search all courts..."
                            : "Type at least 2 characters to search..."
                        : "No courts found"
                }
            />
        </div>
    )
}

export default VenueSelect
